/**
 * Pluckonomics — admin analytics dashboard.
 *
 * Phase E.1 wires:
 *   - KPI card row (8 tiles via GET /api/admin/analytics/kpis)
 *   - Revenue + GMV time-series (60d daily, recharts AreaChart via GET
 *     /api/admin/analytics/series)
 *
 * Phase E.2 wires:
 *   - Platform fees breakdown (left col, GET /fees-breakdown)
 *   - PooBucks comp summary + recent grants feed (right col, GET
 *     /poobucks-summary)
 *
 * Phase E.3 wires (this commit):
 *   - P&L statement table (GET /pnl)
 *   - Customer cohort matrix (GET /cohort?months=6)
 *
 * Display-state contract (mirrors AdminDashboardPage):
 *   - `display_state: "stripe_gated"` tiles always render "—" with an
 *     orange "Activates with payments" badge — even if the backend
 *     accidentally leaks a value on a gated tile.
 *   - `display_state: "live"` with `value: null` renders "—".
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import {
  AreaChart, Area, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend,
} from "recharts";
import PlatformFeesTable from "@/pages/admin/analytics/PlatformFeesTable";
import PooBucksPanel from "@/pages/admin/analytics/PooBucksPanel";
import PnLStatement from "@/pages/admin/analytics/PnLStatement";
import CohortMatrix from "@/pages/admin/analytics/CohortMatrix";

function _formatCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(cents / 100);
}

function _formatInt(n) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US");
}

function _formatPct(n) {
  if (n === null || n === undefined) return "—";
  return `${n}%`;
}

function KpiCard({ label, tile, formatter, testIdSuffix }) {
  const isGated = tile?.display_state === "stripe_gated";
  return (
    <Card
      data-testid={`pluckonomics-kpi-${testIdSuffix}`}
      data-display-state={tile?.display_state || "unknown"}
      style={{ background: "var(--pp-v3-surface)", borderColor: "var(--pp-border)" }}
    >
      <CardContent className="p-5">
        <div className="text-[12px] font-bold uppercase tracking-[0.06em]"
             style={{ color: "var(--pp-text-soft)" }}>
          {label}
        </div>
        <div className="mt-2 text-3xl font-semibold tracking-tight"
             style={{ color: isGated ? "var(--pp-text-soft)" : "var(--pp-text)" }}>
          {isGated ? "—" : formatter(tile?.value)}
        </div>
        {isGated && (
          <div className="mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
               style={{
                 background: "rgba(248, 150, 35, 0.12)",
                 color: "var(--pp-orange)",
               }}
               data-testid={`pluckonomics-kpi-${testIdSuffix}-gated-badge`}>
            {tile?.note || "Activates with payments"}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RevenueGmvChart({ series }) {
  if (!series || !series.points || series.points.length === 0) {
    return (
      <div className="p-12 text-center text-sm" style={{ color: "var(--pp-text-soft)" }}
           data-testid="pluckonomics-chart-empty">
        Loading chart data…
      </div>
    );
  }
  // Convert cents → dollars for axis readability + format MM/DD for X
  const data = series.points.map((p) => ({
    label: p.date.slice(5),               // MM-DD
    gmv:   p.gmv_cents / 100,
    revenue: p.revenue_cents != null ? p.revenue_cents / 100 : null,
  }));
  return (
    <div data-testid="pluckonomics-chart" className="w-full h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="gmvFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#574B8C" stopOpacity={0.32} />
              <stop offset="100%" stopColor="#574B8C" stopOpacity={0.0} />
            </linearGradient>
            <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#F89623" stopOpacity={0.32} />
              <stop offset="100%" stopColor="#F89623" stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--pp-border)" />
          <XAxis dataKey="label" stroke="var(--pp-text-soft)" fontSize={11}
                 tickLine={false} interval={Math.floor(data.length / 8)} />
          <YAxis stroke="var(--pp-text-soft)" fontSize={11} tickLine={false}
                 tickFormatter={(v) => `$${v}`} />
          <Tooltip
            contentStyle={{
              background: "var(--pp-v3-surface)",
              border: "1px solid var(--pp-border)",
              borderRadius: 8,
              color: "var(--pp-text)",
            }}
            formatter={(value, name) => [
              value != null ? `$${Math.round(value).toLocaleString()}` : "—",
              name === "gmv" ? "GMV" : "Revenue",
            ]}
            labelFormatter={(l) => `Date: ${l}`}
          />
          <Legend
            wrapperStyle={{ paddingTop: 8, fontSize: 12, color: "var(--pp-text-soft)" }}
            formatter={(v) => v === "gmv" ? "GMV (booked)" : "Revenue (captured)"}
          />
          <Area type="monotone" dataKey="gmv"     stroke="#574B8C"
                fill="url(#gmvFill)" strokeWidth={2} isAnimationActive={false} />
          {series.revenue_display_state === "live" && (
            <Area type="monotone" dataKey="revenue" stroke="#F89623"
                  fill="url(#revFill)" strokeWidth={2} isAnimationActive={false}
                  connectNulls={false} />
          )}
        </AreaChart>
      </ResponsiveContainer>
      {series.revenue_display_state === "stripe_gated" && (
        <div className="mt-2 text-center text-[12px]" style={{ color: "var(--pp-orange)" }}
             data-testid="pluckonomics-chart-revenue-gated-note">
          Revenue series activates with payments. GMV booked-value shown above.
        </div>
      )}
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const [kpis,    setKpis]    = useState(null);
  const [series,  setSeries]  = useState(null);
  const [fees,    setFees]    = useState(null);  // E.2
  const [poobucks, setPoobucks] = useState(null); // E.2
  const [pnl,     setPnl]     = useState(null);  // E.3
  const [cohort,  setCohort]  = useState(null);  // E.3
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const load = useCallback(async (force = false) => {
    setLoading(true); setError(null);
    try {
      const qs = force ? "?_force_refresh=1" : "";
      const seriesUrl = `/admin/analytics/series${qs ? qs + "&days=60" : "?days=60"}`;
      const cohortUrl = `/admin/analytics/cohort${qs ? qs + "&months=6" : "?months=6"}`;
      const [kRes, sRes, fRes, pRes, plRes, coRes] = await Promise.all([
        api.get(`/admin/analytics/kpis${qs}`),
        api.get(seriesUrl),
        api.get(`/admin/analytics/fees-breakdown${qs}`),
        api.get(`/admin/analytics/poobucks-summary${qs}`),
        api.get(`/admin/analytics/pnl${qs}`),
        api.get(cohortUrl),
      ]);
      setKpis(kRes.data);
      setSeries(sRes.data);
      setFees(fRes.data);
      setPoobucks(pRes.data);
      setPnl(plRes.data);
      setCohort(coRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load analytics");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(false); }, [load]);

  return (
    <div className="space-y-6" data-testid="pluckonomics-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight"
              style={{ color: "var(--pp-text)" }} data-testid="pluckonomics-heading">
            Pluckonomics
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--pp-text-soft)" }}>
            Revenue, conversion + cohort insights. {kpis?.cache_hit ? "(cached)" : ""}
          </p>
        </div>
        <Button
          onClick={() => load(true)}
          disabled={loading}
          variant="outline"
          data-testid="pluckonomics-refresh"
          style={{ borderColor: "var(--pp-border)", color: "var(--pp-text)" }}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </header>

      {error && (
        <div className="rounded-md p-4 text-sm"
             style={{ background: "rgba(248,150,35,0.12)", color: "var(--pp-orange)" }}
             data-testid="pluckonomics-error">
          {String(error)}
        </div>
      )}

      <section data-testid="pluckonomics-kpi-row" className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="GMV (MTD)"          tile={kpis?.gmv_mtd_cents}            formatter={_formatCents} testIdSuffix="gmv-mtd" />
        <KpiCard label="Revenue (MTD)"      tile={kpis?.revenue_mtd_cents}        formatter={_formatCents} testIdSuffix="revenue-mtd" />
        <KpiCard label="Revenue (7d)"       tile={kpis?.revenue_7d_cents}         formatter={_formatCents} testIdSuffix="revenue-7d" />
        <KpiCard label="Avg paid booking"   tile={kpis?.avg_paid_booking_cents}   formatter={_formatCents} testIdSuffix="avg-paid" />
        <KpiCard label="Active bookings"    tile={kpis?.active_bookings}          formatter={_formatInt}   testIdSuffix="active-bookings" />
        <KpiCard label="New signups (30d)"  tile={kpis?.new_signups_30d}          formatter={_formatInt}   testIdSuffix="signups-30d" />
        <KpiCard label="Active providers"   tile={kpis?.active_providers_30d}     formatter={_formatInt}   testIdSuffix="active-providers" />
        <KpiCard label="Completion rate"    tile={kpis?.completion_rate_30d_pct}  formatter={_formatPct}   testIdSuffix="completion-rate" />
      </section>

      <Card style={{ background: "var(--pp-v3-surface)", borderColor: "var(--pp-border)" }}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: "var(--pp-text)" }}>
              Revenue + GMV (60d daily)
            </h2>
          </div>
          <RevenueGmvChart series={series} />
        </CardContent>
      </Card>

      {/* Phase E.2 — Two-column: Platform Fees + PooBucks comps */}
      <section
        className="grid gap-4 grid-cols-1 lg:grid-cols-2"
        data-testid="pluckonomics-e2-row"
      >
        <PlatformFeesTable data={fees} />
        <PooBucksPanel data={poobucks} />
      </section>

      {/* Phase E.3 — P&L statement (full width) */}
      <section data-testid="pluckonomics-e3-pnl-row">
        <PnLStatement data={pnl} />
      </section>

      {/* Phase E.3 — Cohort matrix (full width) */}
      <section data-testid="pluckonomics-e3-cohort-row">
        <CohortMatrix data={cohort} />
      </section>
    </div>
  );
}
